import { DateRangeSearch } from '@routes/__root'
import { RouterProps } from '@tanstack/react-router'
import { buildPredefinedRanges } from 'features/commonUI'
import {
  DEFAULT_STORED_DATA_TTL_DAYS,
  filterRangesWithinTtl,
  getEarliestAllowedDate,
  getLargestTtlBoundRange,
  getMaxRangeDays,
  PREDEFINED_DATE_RANGE_IDS,
} from 'helpers/dateRange'
import { extractData } from 'hooks/api/base'
import { subscriptionQueryOptions } from 'hooks/api/subscriptions'
import { currentUserCacheKey } from 'hooks/api/users'
import { DateTime } from 'luxon'

type PredefinedRangeId = (typeof PREDEFINED_DATE_RANGE_IDS)[number]

type TtlBeforeLoadContext = {
  context: RouterProps['context']
  params: { subscriptionId: string }
}

export type TtlDateRangeOptions = {
  /** Range ids the page accepts at all; defaults to every predefined range. */
  allowedIds?: readonly PredefinedRangeId[]
  /** Range ids the page never accepts, regardless of TTL. */
  excludedIds?: readonly PredefinedRangeId[]
}

async function getStoredDataTtlDays({ context, params }: TtlBeforeLoadContext) {
  try {
    // ensureQueryData reuses the cached subscription; refetching on every navigation would make
    // beforeLoad async each time and remount pages that sync state into the URL on mount.
    const subscription = await context!.queryClient!.ensureQueryData(
      subscriptionQueryOptions(context!.buildRequest!, params.subscriptionId, [])
    )
    return subscription.storedDataTtl ?? DEFAULT_STORED_DATA_TTL_DAYS
  } catch (_error) {
    // Use the conservative default if the subscription cannot be loaded.
    return DEFAULT_STORED_DATA_TTL_DAYS
  }
}

/**
 * The user's configured time zone, which the date pickers use. Luxon's default zone only switches
 * to it after the authenticated layout mounts, so on a cold load it would still be the browser's.
 */
async function getUserTimezone({ context }: TtlBeforeLoadContext) {
  try {
    const user = await context!.queryClient!.ensureQueryData({
      queryKey: currentUserCacheKey,
      queryFn: () => extractData(context!.buildRequest!('currentUserGet')),
    })
    return user?.timezone ?? undefined
  } catch (_error) {
    return undefined
  }
}

export function getTtlSupportedDateRanges(
  storedDataTtlDays: number,
  options: TtlDateRangeOptions = {},
  zone?: string
): PredefinedRangeId[] {
  const allowedIds = options.allowedIds ?? PREDEFINED_DATE_RANGE_IDS
  const candidates = buildPredefinedRanges(DateTime.now().setZone(zone)).filter(
    (range) => allowedIds.includes(range.id) && !options.excludedIds?.includes(range.id)
  )
  return filterRangesWithinTtl(candidates, storedDataTtlDays, zone).map((range) => range.id)
}

export function getTtlClampPeriod(storedDataTtlDays: number, supportedRanges: readonly PredefinedRangeId[]) {
  return getLargestTtlBoundRange(storedDataTtlDays, supportedRanges)?.id ?? 'today'
}

/**
 * Returns the search params to replace when the requested period or custom range reaches
 * further back than the stored data TTL. Empty when the search is already within bounds.
 */
export function enforceTtlDateRangeBounds(
  search: Partial<DateRangeSearch>,
  storedDataTtlDays: number,
  options: TtlDateRangeOptions = {},
  zone?: string
): Partial<DateRangeSearch> {
  const supportedRanges = getTtlSupportedDateRanges(storedDataTtlDays, options, zone)
  const clampToPeriod = getTtlClampPeriod(storedDataTtlDays, supportedRanges)
  const adjustedSearch: Partial<DateRangeSearch> = {}

  if (search.period) {
    if (!supportedRanges.includes(search.period) && PREDEFINED_DATE_RANGE_IDS.includes(search.period)) {
      adjustedSearch.period = clampToPeriod
    }
  } else if (search.since && search.before) {
    const sinceDate = DateTime.fromISO(search.since, { setZone: true })
    const beforeDate = DateTime.fromISO(search.before, { setZone: true })
    // Compare calendar days, not elapsed hours — `before` is often "now" (not end-of-day),
    // which would otherwise add a fractional day and clamp ranges that are exactly at the limit.
    const rangeInDays = beforeDate.startOf('day').diff(sinceDate.startOf('day'), 'days').days
    const earliestAllowedDate = getEarliestAllowedDate(storedDataTtlDays, sinceDate.zoneName ?? 'UTC')

    if (rangeInDays > getMaxRangeDays(storedDataTtlDays) || sinceDate < earliestAllowedDate) {
      adjustedSearch.since = undefined
      adjustedSearch.before = undefined
      adjustedSearch.period = clampToPeriod
    }
  }

  return adjustedSearch
}

export async function getTtlDateRangeAdjustment(
  ctx: TtlBeforeLoadContext & { search: Partial<DateRangeSearch> },
  options: TtlDateRangeOptions = {}
): Promise<Partial<DateRangeSearch>> {
  if (!ctx.search.period && !(ctx.search.since && ctx.search.before)) {
    return {}
  }
  const [storedDataTtlDays, zone] = await Promise.all([getStoredDataTtlDays(ctx), getUserTimezone(ctx)])
  return enforceTtlDateRangeBounds(ctx.search, storedDataTtlDays, options, zone)
}

/** Merges a TTL adjustment into the search, dropping a stale custom range when a period replaces it. */
export function applyTtlDateRangeAdjustment<T extends Partial<DateRangeSearch>>(
  old: T,
  adjustedSearch: Partial<DateRangeSearch>
): T {
  const newSearch = { ...old, ...adjustedSearch }
  if (adjustedSearch.period && !adjustedSearch.since) {
    delete newSearch.since
    delete newSearch.before
  }
  return newSearch
}
